import { EpiLink } from '@api/types';
import { BlockProperty, SmartLink } from '@components';
import {
  useAppSelector,
  useAuth,
  useProductOrderCount,
  useTechnicalMessageCount,
  useWarrantyClaimsCount,
} from '@hooks';
import { Badge, Box } from '@mui/material';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { getSpecialPage } from '@store/app/app.slice';
import { colors } from '@themes/colors';
import './NavigationBlock.css';

export type NavigationBlockProps = {
  firstName?: string;
  lastName?: string;
  primaryLinks?: EpiLink[];
};
/**
 * Navigation Block - renders site Navigation Bar.
 * Component is connected to the Redux Store
 */
export const NavigationBlock = (props: NavigationBlockProps) => {
  const { primaryLinks } = props;
  const dealerTechnicalMessagePage = useAppSelector(
    getSpecialPage('dealerTechnicalMessageListPage')
  );
  const beremaTechnicalMessagePage = useAppSelector(
    getSpecialPage('beremaTechnicalMessageListPage')
  );
  const dealerWarrantyClaimPage = useAppSelector(getSpecialPage('dealerWarrantyListPage'));
  const beremaWarrantyListPage = useAppSelector(getSpecialPage('beremaWarrantyListPage'));
  const productOrderPage = useAppSelector(getSpecialPage('productOrderRequestListPage'));
  const { isAuthenticated, isDealerUser } = useAuth();
  const hasTechnicalMessageLinks = useMemo(
    () =>
      primaryLinks?.some(
        (link) =>
          (dealerTechnicalMessagePage && link.href?.includes(dealerTechnicalMessagePage)) ||
          (beremaTechnicalMessagePage && link.href?.includes(beremaTechnicalMessagePage))
      ),
    [primaryLinks, dealerTechnicalMessagePage, beremaTechnicalMessagePage]
  );

  const hasWarrantyClaimLinks = useMemo(
    () =>
      primaryLinks?.some(
        (link) =>
          (dealerWarrantyClaimPage && link.href?.includes(dealerWarrantyClaimPage)) ||
          (beremaWarrantyListPage && link.href?.includes(beremaWarrantyListPage))
      ),
    [primaryLinks, dealerWarrantyClaimPage, beremaWarrantyListPage]
  );

  const hasProductOrderLinks = useMemo(
    () => primaryLinks?.some((link) => productOrderPage && link.href?.includes(productOrderPage)),
    [primaryLinks, productOrderPage]
  );

  const { count: totalNewMessages } = useTechnicalMessageCount({
    skip: !hasTechnicalMessageLinks && !isAuthenticated,
  });

  const { count: totalNewWarrantyClaims } = useWarrantyClaimsCount({
    skip: !hasWarrantyClaimLinks && !isAuthenticated,
  });

  const { count: totalNewOrders } = useProductOrderCount({
    skip: !hasProductOrderLinks && !isDealerUser,
  });

  const [showLeftButton, setShowLeftButton] = useState(false);
  const [showRightButton, setShowRightButton] = useState(false);
  const navContentRef = useRef<HTMLDivElement>(null);

  const getBadgeContent = (link: EpiLink) => {
    if (dealerTechnicalMessagePage && link.href?.includes(dealerTechnicalMessagePage)) {
      return totalNewMessages || 0;
    }
    if (
      (dealerWarrantyClaimPage && link.href?.includes(dealerWarrantyClaimPage)) ||
      (beremaWarrantyListPage && link.href?.includes(beremaWarrantyListPage))
    ) {
      return totalNewWarrantyClaims || 0;
    }
    if (productOrderPage && link.href?.includes(productOrderPage)) {
      return totalNewOrders || 0;
    }
    return 0;
  };

  const updateScrollButtons = useCallback(() => {
    if (navContentRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = navContentRef.current;
      setShowLeftButton(scrollLeft > 0);
      setShowRightButton(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  const scrollLeft = () => {
    navContentRef.current?.scrollBy({ left: -200, behavior: 'smooth' });
  };
  const currentNavContentRef = navContentRef.current;
  const scrollRight = () => {
    currentNavContentRef?.scrollBy({ left: 200, behavior: 'smooth' });
  };

  useEffect(() => {
    window.addEventListener('resize', updateScrollButtons);
    currentNavContentRef?.addEventListener('scroll', updateScrollButtons);
    updateScrollButtons();

    return () => {
      window.removeEventListener('resize', updateScrollButtons);
      currentNavContentRef?.removeEventListener('scroll', updateScrollButtons);
    };
  }, [updateScrollButtons, currentNavContentRef]);

  return (
    <Box
      component='nav'
      className='navbar'
      sx={{
        borderBottom: `1px solid ${colors.primary.mainGreen}`,
      }}
    >
      <div className='navbar__content'>
        <BlockProperty property='primaryLinks'>
          <div className='nav-container'>
            {showLeftButton && (
              <button className='scroll-btn left' onClick={scrollLeft}>
                ‹
              </button>
            )}
            <Box className='navbar__left-side' ref={navContentRef}>
              {primaryLinks?.map((link, index) => {
                const badgeContent = (
                  <Badge
                    badgeContent={getBadgeContent(link)}
                    color='error'
                    max={99}
                    sx={{ '& .MuiBadge-badge': { top: 12, right: 10 } }}
                  >
                    <SmartLink {...link} className='navbar__nav-link' />
                  </Badge>
                );
                return (
                  <Fragment key={index}>
                    {getBadgeContent(link) > 0 ? (
                      badgeContent
                    ) : (
                      <SmartLink {...link} className='navbar__nav-link' />
                    )}
                  </Fragment>
                );
              })}
            </Box>
            {showRightButton && (
              <button className='scroll-btn right' onClick={scrollRight}>
                ›
              </button>
            )}
          </div>
        </BlockProperty>
      </div>
    </Box>
  );
};
